import { useState, useEffect, useRef, useCallback } from "react";
import { io } from "socket.io-client";

const SOCKET_URL = process.env.REACT_APP_SOCKET_URL || "http://localhost:3001";

// ─── Styles ──────────────────────────────────────────────────────────────────
const G = {
  bg: "#0c0c0e",
  surface: "#141416",
  surfaceHover: "#1a1a1e",
  border: "#222228",
  borderLight: "#2e2e38",
  accent: "#ff4d4d",
  accentDim: "rgba(255,77,77,0.12)",
  accentGlow: "rgba(255,77,77,0.25)",
  green: "#3dffa0",
  greenDim: "rgba(61,255,160,0.1)",
  yellow: "#ffd93d",
  text: "#f0f0f0",
  textMid: "#888",
  textDim: "#444",
  fontDisplay: "'Syne', sans-serif",
  fontMono: "'DM Mono', monospace",
};

const globalStyles = `
  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
  html, body { height: 100%; background: ${G.bg}; color: ${G.text}; }
  body { font-family: ${G.fontMono}; overflow: hidden; }
  #root { height: 100vh; display: flex; flex-direction: column; }

  ::-webkit-scrollbar { width: 4px; }
  ::-webkit-scrollbar-track { background: transparent; }
  ::-webkit-scrollbar-thumb { background: ${G.border}; border-radius: 2px; }

  @keyframes fadeUp {
    from { opacity: 0; transform: translateY(16px); }
    to   { opacity: 1; transform: translateY(0); }
  }
  @keyframes fadeIn {
    from { opacity: 0; } to { opacity: 1; }
  }
  @keyframes pulse {
    0%, 100% { opacity: 1; } 50% { opacity: 0.3; }
  }
  @keyframes spin {
    from { transform: rotate(0deg); } to { transform: rotate(360deg); }
  }
  @keyframes blink {
    0%, 100% { opacity: 1; } 50% { opacity: 0; }
  }
  @keyframes slideIn {
    from { opacity: 0; transform: translateX(-10px); }
    to   { opacity: 1; transform: translateX(0); }
  }
  @keyframes slideInRight {
    from { opacity: 0; transform: translateX(10px); }
    to   { opacity: 1; transform: translateX(0); }
  }
  @keyframes typingBounce {
    0%, 80%, 100% { transform: translateY(0); }
    40% { transform: translateY(-6px); }
  }
  @keyframes scanline {
    0% { transform: translateY(-100%); }
    100% { transform: translateY(100vh); }
  }
  @keyframes connectedFlash {
    0% { background: ${G.greenDim}; }
    50% { background: rgba(61,255,160,0.2); }
    100% { background: transparent; }
  }

  /* Responsive Utilities */
  .hide-on-mobile { display: inline; }
  .landing-title { font-size: 72px; letter-spacing: -3px; }
  .action-row { display: flex; gap: 10px; width: 100%; }
  .chat-container { flex: 1; display: flex; flex-direction: column; max-width: 800px; width: 100%; margin: 0 auto; border-left: 1px solid ${G.border}; border-right: 1px solid ${G.border}; }
  .chat-header { display: flex; align-items: center; justify-content: space-between; padding: 14px 20px; border-bottom: 1px solid ${G.border}; background: ${G.surface}; flex-shrink: 0; }
  
  @media (max-width: 600px) {
    .hide-on-mobile { display: none !important; }
    .landing-title { font-size: 48px !important; letter-spacing: -2px !important; }
    .action-row { flex-direction: column; gap: 12px !important; }
    .action-row button { width: 100%; padding: 16px !important; }
    .chat-container { max-width: 100% !important; border-left: none !important; border-right: none !important; }
    .chat-header { padding: 12px 16px !important; }
    .chat-header button { padding: 8px 12px !important; font-size: 11px !important; }
  }
`;

// ─── Socket singleton ─────────────────────────────────────────────────────────
let socketInstance = null;
function getSocket() {
  if (!socketInstance) {
    socketInstance = io(SOCKET_URL, { autoConnect: false, reconnectionAttempts: 5 });
  }
  return socketInstance;
}

// ─── Components ──────────────────────────────────────────────────────────────

function StatBadge({ label, value, color }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
      <span style={{
        width: 6, height: 6, borderRadius: "50%",
        background: color, display: "inline-block",
        boxShadow: `0 0 6px ${color}`,
        animation: "pulse 2s infinite",
      }} />
      <span style={{ fontFamily: G.fontMono, fontSize: 11, color: G.textMid }}>
        <span style={{ color, fontWeight: 500 }}>{value}</span> {label}
      </span>
    </div>
  );
}

function TypingIndicator() {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 16px", animation: "slideIn 0.2s ease" }}>
      <div style={{ display: "flex", gap: 4, alignItems: "center" }}>
        {[0, 1, 2].map(i => (
          <span key={i} style={{
            width: 6, height: 6, borderRadius: "50%",
            background: G.textMid,
            display: "inline-block",
            animation: `typingBounce 1.2s ${i * 0.2}s infinite ease-in-out`,
          }} />
        ))}
      </div>
      <span style={{ fontSize: 11, color: G.textDim, fontStyle: "italic" }}>stranger is typing</span>
    </div>
  );
}

// ─── Landing Screen ───────────────────────────────────────────────────────────
function LandingScreen({ onStart }) {
  const [name, setName] = useState("");
  const [focused, setFocused] = useState(false);
  const inputRef = useRef(null);

  const handleSubmit = () => {
    const trimmed = name.trim();
    if (!trimmed) return;
    onStart(trimmed);
  };

  return (
    <div style={{
      flex: 1, display: "flex", flexDirection: "column",
      alignItems: "center", justifyContent: "center",
      padding: 24, position: "relative", overflow: "hidden",
    }}>
      {/* Grid bg */}
      <div style={{
        position: "absolute", inset: 0, zIndex: 0,
        backgroundImage: `
          linear-gradient(${G.border} 1px, transparent 1px),
          linear-gradient(90deg, ${G.border} 1px, transparent 1px)
        `,
        backgroundSize: "48px 48px",
        opacity: 0.4,
      }} />

      {/* Glow */}
      <div style={{
        position: "absolute", top: "30%", left: "50%",
        transform: "translate(-50%, -50%)",
        width: 400, height: 400, borderRadius: "50%",
        background: `radial-gradient(circle, ${G.accentGlow} 0%, transparent 70%)`,
        pointerEvents: "none", zIndex: 0,
      }} />

      <div style={{ position: "relative", zIndex: 1, textAlign: "center", animation: "fadeUp 0.7s ease", maxWidth: 480, width: "100%" }}>
        {/* Logo */}
        <div style={{ marginBottom: 8 }}>
          <span className="landing-title" style={{
            fontFamily: G.fontDisplay,
            fontWeight: 800,
            color: G.text,
            lineHeight: 1,
            display: "block",
          }}>
            STR<span style={{ color: G.accent }}>∅</span>NGR
          </span>
          <span style={{
            fontFamily: G.fontMono, fontSize: 11,
            color: G.textMid, letterSpacing: "4px",
            textTransform: "uppercase",
          }}>
            talk to anyone, anywhere
          </span>
        </div>

        <div style={{ margin: "48px 0 32px", display: "flex", flexDirection: "column", gap: 6 }}>
          {[
            "🎲  matched randomly with a real stranger",
            "👤  anonymous by default",
            "⚡  skip to meet someone new instantly",
          ].map((feat, i) => (
            <div key={i} style={{
              fontFamily: G.fontMono, fontSize: 12, color: G.textMid,
              animation: `fadeUp 0.6s ${0.2 + i * 0.1}s both ease`,
            }}>{feat}</div>
          ))}
        </div>

        <div className="action-row" style={{ animation: "fadeUp 0.6s 0.5s both ease" }}>
          <div style={{ position: "relative", flex: 1, width: "100%" }}>
            <input
              ref={inputRef}
              value={name}
              onChange={e => setName(e.target.value.slice(0, 24))}
              onKeyDown={e => e.key === "Enter" && handleSubmit()}
              onFocus={() => setFocused(true)}
              onBlur={() => setFocused(false)}
              placeholder="your name or alias…"
              autoFocus
              style={{
                width: "100%", padding: "14px 16px",
                background: G.surface,
                border: `1px solid ${focused ? G.accent : G.border}`,
                borderRadius: 8,
                color: G.text,
                fontFamily: G.fontMono, fontSize: 14,
                outline: "none",
                transition: "border-color 0.2s, box-shadow 0.2s",
                boxShadow: focused ? `0 0 0 3px ${G.accentDim}` : "none",
              }}
            />
          </div>
          <button
            onClick={handleSubmit}
            disabled={!name.trim()}
            style={{
              padding: "14px 28px",
              background: name.trim() ? G.accent : G.border,
              border: "none", borderRadius: 8,
              color: name.trim() ? "#000" : G.textDim,
              fontFamily: G.fontDisplay, fontWeight: 700,
              fontSize: 14, cursor: name.trim() ? "pointer" : "not-allowed",
              transition: "all 0.2s",
              letterSpacing: "0.5px",
              whiteSpace: "nowrap",
            }}
          >
            Find Stranger →
          </button>
        </div>

        <div style={{
          marginTop: 20, fontFamily: G.fontMono, fontSize: 11,
          color: G.textDim, animation: "fadeUp 0.6s 0.6s both ease",
        }}>
          be respectful · no personal info · exit anytime
        </div>
      </div>

      {/* Feedback Link */}
      <div style={{
        position: "absolute", bottom: 20, left: 0, right: 0,
        textAlign: "center", zIndex: 10, animation: "fadeIn 1s 1s both ease"
      }}>
        <a 
          href="https://forms.gle/VWLC8FGFe74bXqwC7" 
          target="_blank" 
          rel="noopener noreferrer"
          style={{
            fontFamily: G.fontMono, fontSize: 12,
            color: G.textMid, textDecoration: "none",
            borderBottom: `1px dashed ${G.textDim}`,
            paddingBottom: 2, transition: "all 0.2s"
          }}
          onMouseEnter={e => { e.target.style.color = G.text; e.target.style.borderBottomColor = G.textMid; }}
          onMouseLeave={e => { e.target.style.color = G.textMid; e.target.style.borderBottomColor = G.textDim; }}
        >
          Have feedback? Let us know
        </a>
      </div>
    </div>
  );
}

// ─── Waiting Screen ───────────────────────────────────────────────────────────
function WaitingScreen({ stats, onCancel }) {
  const [dots, setDots] = useState("");
  const [elapsed, setElapsed] = useState(0);

  useEffect(() => {
    const d = setInterval(() => setDots(p => p.length >= 3 ? "" : p + "."), 500);
    const e = setInterval(() => setElapsed(p => p + 1), 1000);
    return () => { clearInterval(d); clearInterval(e); };
  }, []);

  return (
    <div style={{
      flex: 1, display: "flex", flexDirection: "column",
      alignItems: "center", justifyContent: "center", gap: 32,
    }}>
      {/* Spinner ring */}
      <div style={{ position: "relative", width: 80, height: 80 }}>
        <div style={{
          position: "absolute", inset: 0,
          border: `2px solid ${G.border}`,
          borderTop: `2px solid ${G.accent}`,
          borderRadius: "50%",
          animation: "spin 1s linear infinite",
        }} />
        <div style={{
          position: "absolute", inset: 8,
          border: `1px solid ${G.borderLight}`,
          borderBottom: `1px solid ${G.accentDim}`,
          borderRadius: "50%",
          animation: "spin 2s linear infinite reverse",
        }} />
        <div style={{
          position: "absolute", inset: 0,
          display: "flex", alignItems: "center", justifyContent: "center",
          fontFamily: G.fontMono, fontSize: 11, color: G.textMid,
        }}>
          {elapsed}s
        </div>
      </div>

      <div style={{ textAlign: "center" }}>
        <div style={{
          fontFamily: G.fontDisplay, fontSize: 22, fontWeight: 700,
          color: G.text, marginBottom: 8,
        }}>
          Finding your stranger{dots}
        </div>
        <div style={{ fontFamily: G.fontMono, fontSize: 12, color: G.textMid }}>
          {stats.waiting > 1
            ? `${stats.waiting} people in queue — connecting…`
            : "waiting for someone to join…"}
        </div>
      </div>

      {stats.online > 0 && (
        <div style={{ display: "flex", gap: 20 }}>
          <StatBadge label="online" value={stats.online} color={G.green} />
          <StatBadge label="chatting" value={stats.chatting} color={G.yellow} />
          <StatBadge label="waiting" value={stats.waiting} color={G.accent} />
        </div>
      )}

      <button
        onClick={onCancel}
        style={{
          padding: "10px 24px", background: "transparent",
          border: `1px solid ${G.border}`, borderRadius: 6,
          color: G.textMid, fontFamily: G.fontMono, fontSize: 13,
          cursor: "pointer", transition: "all 0.2s",
        }}
        onMouseEnter={e => { e.target.style.borderColor = G.accent; e.target.style.color = G.accent; }}
        onMouseLeave={e => { e.target.style.borderColor = G.border; e.target.style.color = G.textMid; }}
      >
        ← cancel
      </button>
    </div>
  );
}

// ─── Chat Screen ──────────────────────────────────────────────────────────────
function ChatScreen({ socket, myName, strangerName, socketId, onNext, onLeave }) {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [strangerTyping, setStrangerTyping] = useState(false);
  const [strangerLeft, setStrangerLeft] = useState(false);
  const [flashConnected, setFlashConnected] = useState(true);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const typingTimeout = useRef(null);
  const isTypingRef = useRef(false);

  useEffect(() => {
    setMessages([{ type: "system", text: `You're now chatting with ${strangerName}. Say hi!`, id: "sys-0" }]);
    setFlashConnected(true);
    setTimeout(() => setFlashConnected(false), 800);
    inputRef.current?.focus();

    const handleMessage = (msg) => {
      setMessages(prev => [...prev, { ...msg, type: "stranger" }]);
    };
    const handleTyping = ({ isTyping }) => setStrangerTyping(isTyping);
    const handleLeft = () => {
      setStrangerLeft(true);
      setStrangerTyping(false);
      setMessages(prev => [...prev, { type: "system", text: `${strangerName} has disconnected.`, id: "sys-left" }]);
    };

    socket.on("receive_message", handleMessage);
    socket.on("stranger_typing", handleTyping);
    socket.on("stranger_left", handleLeft);

    return () => {
      socket.off("receive_message", handleMessage);
      socket.off("stranger_typing", handleTyping);
      socket.off("stranger_left", handleLeft);
    };
  }, [socket, strangerName]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, strangerTyping]);

  const handleInputChange = (e) => {
    setInput(e.target.value);

    if (!isTypingRef.current) {
      socket.emit("typing", { isTyping: true });
      isTypingRef.current = true;
    }
    clearTimeout(typingTimeout.current);
    typingTimeout.current = setTimeout(() => {
      socket.emit("typing", { isTyping: false });
      isTypingRef.current = false;
    }, 1500);
  };

  const sendMessage = () => {
    const text = input.trim();
    if (!text || strangerLeft) return;
    socket.emit("send_message", { text });
    socket.emit("typing", { isTyping: false });
    isTypingRef.current = false;
    clearTimeout(typingTimeout.current);
    setMessages(prev => [...prev, { type: "me", text, id: Date.now() + "" }]);
    setInput("");
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendMessage(); }
  };

  return (
    <div className="chat-container" style={{
      animation: flashConnected ? "connectedFlash 0.8s ease" : "none",
    }}>
      {/* Header */}
      <div className="chat-header">
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <div style={{
            width: 36, height: 36, borderRadius: 8,
            background: G.accentDim,
            border: `1px solid ${G.accent}`,
            display: "flex", alignItems: "center", justifyContent: "center",
            fontFamily: G.fontDisplay, fontWeight: 700,
            color: G.accent, fontSize: 14,
          }}>
            {strangerName[0]?.toUpperCase()}
          </div>
          <div>
            <div style={{ fontFamily: G.fontDisplay, fontSize: 15, fontWeight: 600, color: G.text }}>
              {strangerName}
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 5, marginTop: 2 }}>
              <span style={{
                width: 5, height: 5, borderRadius: "50%",
                background: strangerLeft ? G.textDim : G.green,
                display: "inline-block",
              }} />
              <span style={{ fontFamily: G.fontMono, fontSize: 10, color: G.textMid }}>
                {strangerLeft ? "disconnected" : "online"}
              </span>
            </div>
          </div>
        </div>

        <div style={{ display: "flex", gap: 8 }}>
          <button
            onClick={onNext}
            style={{
              padding: "8px 16px",
              background: "transparent",
              border: `1px solid ${G.border}`,
              borderRadius: 6,
              color: G.textMid,
              fontFamily: G.fontMono, fontSize: 12,
              cursor: "pointer", transition: "all 0.15s",
            }}
            onMouseEnter={e => { e.currentTarget.style.borderColor = G.yellow; e.currentTarget.style.color = G.yellow; }}
            onMouseLeave={e => { e.currentTarget.style.borderColor = G.border; e.currentTarget.style.color = G.textMid; }}
          >
            ⚡ next
          </button>
          <button
            onClick={onLeave}
            style={{
              padding: "8px 16px",
              background: "transparent",
              border: `1px solid ${G.border}`,
              borderRadius: 6,
              color: G.textMid,
              fontFamily: G.fontMono, fontSize: 12,
              cursor: "pointer", transition: "all 0.15s",
            }}
            onMouseEnter={e => { e.currentTarget.style.borderColor = G.accent; e.currentTarget.style.color = G.accent; }}
            onMouseLeave={e => { e.currentTarget.style.borderColor = G.border; e.currentTarget.style.color = G.textMid; }}
          >
            ✕ stop
          </button>
        </div>
      </div>

      {/* Messages */}
      <div style={{
        flex: 1, overflowY: "auto",
        padding: "20px 20px 8px",
        display: "flex", flexDirection: "column", gap: 4,
      }}>
        {messages.map((msg) => {
          if (msg.type === "system") {
            return (
              <div key={msg.id} style={{
                textAlign: "center", padding: "8px 0",
                fontFamily: G.fontMono, fontSize: 11, color: G.textDim,
                animation: "fadeIn 0.3s ease",
              }}>
                — {msg.text} —
              </div>
            );
          }
          const isMe = msg.type === "me";
          return (
            <div key={msg.id} style={{
              display: "flex",
              justifyContent: isMe ? "flex-end" : "flex-start",
              marginBottom: 6,
              animation: isMe ? "slideInRight 0.2s ease" : "slideIn 0.2s ease",
            }}>
              {!isMe && (
                <div style={{
                  width: 26, height: 26, borderRadius: 6, flexShrink: 0,
                  background: G.accentDim, border: `1px solid ${G.accent}`,
                  display: "flex", alignItems: "center", justifyContent: "center",
                  fontFamily: G.fontDisplay, fontSize: 10, color: G.accent,
                  marginRight: 8, marginTop: 2,
                }}>
                  {(msg.username || strangerName)[0]?.toUpperCase()}
                </div>
              )}
              <div style={{ maxWidth: "70%" }}>
                <div style={{
                  padding: "10px 14px",
                  background: isMe ? G.accent : G.surface,
                  color: isMe ? "#000" : G.text,
                  borderRadius: isMe ? "12px 12px 2px 12px" : "12px 12px 12px 2px",
                  fontFamily: G.fontMono, fontSize: 13,
                  lineHeight: 1.5,
                  border: isMe ? "none" : `1px solid ${G.border}`,
                  wordBreak: "break-word",
                }}>
                  {msg.text}
                </div>
                <div style={{
                  fontSize: 10, color: G.textDim, marginTop: 3,
                  textAlign: isMe ? "right" : "left",
                  fontFamily: G.fontMono,
                }}>
                  {new Date(msg.timestamp || Date.now()).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                </div>
              </div>
            </div>
          );
        })}

        {strangerTyping && <TypingIndicator />}
        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <div style={{
        padding: "12px 16px",
        borderTop: `1px solid ${G.border}`,
        background: G.surface, flexShrink: 0,
        display: "flex", gap: 10, alignItems: "flex-end",
      }}>
        <textarea
          ref={inputRef}
          value={input}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          placeholder={strangerLeft ? "stranger has left…" : "type a message… (Enter to send)"}
          disabled={strangerLeft}
          rows={1}
          style={{
            flex: 1, padding: "11px 14px",
            background: strangerLeft ? "transparent" : G.bg,
            border: `1px solid ${G.border}`,
            borderRadius: 8,
            color: G.text,
            fontFamily: G.fontMono, fontSize: 13,
            outline: "none", resize: "none",
            lineHeight: 1.5, maxHeight: 120, overflowY: "auto",
            transition: "border-color 0.2s",
          }}
          onFocus={e => e.target.style.borderColor = G.accent}
          onBlur={e => e.target.style.borderColor = G.border}
        />
        <button
          onClick={sendMessage}
          disabled={!input.trim() || strangerLeft}
          style={{
            padding: "11px 20px",
            background: input.trim() && !strangerLeft ? G.accent : G.border,
            border: "none", borderRadius: 8,
            color: input.trim() && !strangerLeft ? "#000" : G.textDim,
            fontFamily: G.fontDisplay, fontWeight: 700,
            fontSize: 13, cursor: input.trim() && !strangerLeft ? "pointer" : "not-allowed",
            transition: "all 0.15s", whiteSpace: "nowrap",
          }}
        >
          Send →
        </button>
      </div>

      {strangerLeft && (
        <div style={{
          padding: "12px 16px",
          borderTop: `1px solid ${G.border}`,
          background: G.surface,
          display: "flex", gap: 10, justifyContent: "center",
        }}>
          <button onClick={onNext} style={{
            padding: "10px 24px",
            background: G.accent, border: "none", borderRadius: 7,
            color: "#000", fontFamily: G.fontDisplay, fontWeight: 700,
            fontSize: 13, cursor: "pointer",
          }}>⚡ Meet someone new</button>
          <button onClick={onLeave} style={{
            padding: "10px 24px",
            background: "transparent", border: `1px solid ${G.border}`, borderRadius: 7,
            color: G.textMid, fontFamily: G.fontMono, fontSize: 13, cursor: "pointer",
          }}>← Back to home</button>
        </div>
      )}
    </div>
  );
}

// ─── App ──────────────────────────────────────────────────────────────────────
export default function App() {
  const [screen, setScreen] = useState("landing"); // landing | waiting | chat
  const [myName, setMyName] = useState("");
  const [strangerName, setStrangerName] = useState("");
  const [stats, setStats] = useState({ online: 0, waiting: 0, chatting: 0 });
  const [socketId, setSocketId] = useState("");
  const socketRef = useRef(null);

  useEffect(() => {
    const style = document.createElement("style");
    style.textContent = globalStyles;
    document.head.appendChild(style);
    return () => document.head.removeChild(style);
  }, []);

  useEffect(() => {
    const socket = getSocket();
    socketRef.current = socket;
    socket.connect();

    socket.on("connect", () => setSocketId(socket.id));
    socket.on("stats", (s) => setStats(s));
    socket.on("waiting", () => setScreen("waiting"));
    socket.on("matched", ({ strangerName: sName }) => {
      setStrangerName(sName);
      setScreen("chat");
    });
    socket.on("disconnect", () => {
      setScreen("landing");
    });

    return () => {
      socket.off("connect");
      socket.off("stats");
      socket.off("waiting");
      socket.off("matched");
      socket.off("disconnect");
    };
  }, []);

  const handleStart = useCallback((name) => {
    setMyName(name);
    socketRef.current.emit("join_queue", { username: name });
  }, []);

  const handleNext = useCallback(() => {
    socketRef.current.emit("next_stranger");
  }, []);

  const handleLeave = useCallback(() => {
    socketRef.current.emit("leave_chat");
    setScreen("landing");
  }, []);

  const handleCancel = useCallback(() => {
    socketRef.current.emit("leave_chat");
    setScreen("landing");
  }, []);

  return (
    <div style={{ height: "100vh", display: "flex", flexDirection: "column", background: G.bg }}>
      {/* Top bar */}
      <div style={{
        display: "flex", alignItems: "center", justifyContent: "space-between",
        padding: "10px 24px",
        borderBottom: `1px solid ${G.border}`,
        background: G.surface, flexShrink: 0,
      }}>
        <span style={{
          fontFamily: G.fontDisplay, fontWeight: 800,
          fontSize: 16, color: G.text, letterSpacing: "-0.5px",
        }}>
          STR<span style={{ color: G.accent }}>∅</span>NGR
        </span>
        <div style={{ display: "flex", gap: 16 }}>
          <StatBadge label="online" value={stats.online} color={G.green} />
          <StatBadge label="chatting" value={stats.chatting} color={G.yellow} />
          {screen === "chat" && myName && (
            <span className="hide-on-mobile" style={{ fontFamily: G.fontMono, fontSize: 11, color: G.textMid }}>
              you: <span style={{ color: G.text }}>{myName}</span>
            </span>
          )}
        </div>
      </div>

      {/* Screens */}
      {screen === "landing" && <LandingScreen onStart={handleStart} />}
      {screen === "waiting" && <WaitingScreen stats={stats} onCancel={handleCancel} />}
      {screen === "chat" && (
        <ChatScreen
          socket={socketRef.current}
          myName={myName}
          strangerName={strangerName}
          socketId={socketId}
          onNext={handleNext}
          onLeave={handleLeave}
        />
      )}
    </div>
  );
}
